import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Newspaper, Broadcast, ArrowUpRight, Trophy, GraduationCap, CalendarCheck,
  Megaphone, Warning, Medal, Confetti, Star, Fire, CaretLeft, CaretRight, BellSimpleRinging,
  Rows as RowsIcon, SquaresFour, Lightning, DownloadSimple,
} from "@phosphor-icons/react";
import { getNewsColleges, getNewsFeed } from "../lib/api";
import { SubscribeButton } from "./SubscribeButton";
import { AnimatedBackdrop, ParticleField } from "./SectionFX";
import { NewsroomTV } from "./NewsroomTV";

const PHRASES = ["Subscribe to live news feed", "Never miss a notification"];

// Typewriter caption above the Notify button: types each phrase char-by-char
// (rising up from the button), holds 5s, fades, then loops to the next phrase.
function LiveFeedTyper() {
  const [idx, setIdx] = useState(0);
  const [count, setCount] = useState(0);
  const [fading, setFading] = useState(false);
  const phrase = PHRASES[idx];
  useEffect(() => {
    setCount(0); setFading(false);
    let c = 0;
    const timers = [];
    const type = setInterval(() => {
      c += 1; setCount(c);
      if (c >= phrase.length) {
        clearInterval(type);
        timers.push(setTimeout(() => {
          setFading(true);
          timers.push(setTimeout(() => setIdx((i) => (i + 1) % PHRASES.length), 600));
        }, 5000));
      }
    }, 55);
    return () => { clearInterval(type); timers.forEach(clearTimeout); };
  }, [idx, phrase.length]);
  return (
    <div className={`flex items-center gap-1.5 h-5 -mt-2.5 transition-opacity duration-500 ${fading ? "opacity-0" : "opacity-100"}`} data-testid="newsroom-typer">
      <BellSimpleRinging size={15} weight="fill" className="text-[#6043EF] shrink-0" />
      <span className="text-sm font-bold uppercase tracking-wider text-[#6043EF] whitespace-nowrap">
        {[...phrase.slice(0, count)].map((ch, i) => (
          <span key={i} style={{ display: "inline-block", whiteSpace: "pre", animation: "crRise .25s ease both" }}>{ch}</span>
        ))}
        <span className="inline-block w-[2px] h-3.5 -mb-0.5 bg-[#6043EF] ml-0.5" style={{ animation: "crBlink 1s step-end infinite" }} />
      </span>
    </div>
  );
}

function shade(hex, amt) {
  const n = parseInt((hex || "#888888").replace("#", ""), 16);
  let r = (n >> 16) + Math.round(255 * amt);
  let g = ((n >> 8) & 255) + Math.round(255 * amt);
  let b = (n & 255) + Math.round(255 * amt);
  r = Math.max(0, Math.min(255, r)); g = Math.max(0, Math.min(255, g)); b = Math.max(0, Math.min(255, b));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

// Authority brand accents (light theme).
const AUTH = {
  nmc: { label: "NMC", full: "National Medical Commission",   dot: "#0f766e", soft: "bg-teal-50 text-teal-700 ring-teal-200" },
  mcc: { label: "MCC", full: "Medical Counselling Committee", dot: "#b45309", soft: "bg-amber-50 text-amber-700 ring-amber-200" },
  nbe: { label: "NBE", full: "National Board of Examinations",dot: "#6d28d9", soft: "bg-violet-50 text-violet-700 ring-violet-200" },
  nta: { label: "NTA", full: "National Testing Agency",       dot: "#be123c", soft: "bg-rose-50 text-rose-700 ring-rose-200" },
};
const authMeta = (c) => AUTH[c] || { label: (c || "").toUpperCase(), full: c, dot: "#6043EF", soft: "bg-zinc-100 text-zinc-600 ring-zinc-200" };

const TPL = {
  result:      { label: "Result",    Icon: Trophy,        cls: "bg-emerald-50 text-emerald-700" },
  admission:   { label: "Admission", Icon: GraduationCap, cls: "bg-sky-50 text-sky-700" },
  schedule:    { label: "Schedule",  Icon: CalendarCheck, cls: "bg-indigo-50 text-indigo-700" },
  notice:      { label: "Notice",    Icon: Megaphone,     cls: "bg-slate-100 text-slate-600" },
  alert:       { label: "Alert",     Icon: Warning,       cls: "bg-rose-50 text-rose-700" },
  achievement: { label: "Update",    Icon: Medal,         cls: "bg-orange-50 text-orange-700" },
  event:       { label: "Event",     Icon: Confetti,      cls: "bg-pink-50 text-pink-700" },
  ranking:     { label: "Ranking",   Icon: Star,          cls: "bg-violet-50 text-violet-700" },
  breaking:    { label: "Breaking",  Icon: Fire,          cls: "bg-red-50 text-red-700" },
};
const tpl = (t) => TPL[t] || TPL.notice;

function whenLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.max(1, (Date.now() - d.getTime()) / 1000);
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const days = Math.floor(s / 86400);
  if (days < 7) return `${days}d ago`;
  if (days < 35) return `${Math.floor(days / 7)}w ago`;
  return "";
}

function ordinal(n) {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// Full published date: "9th Jul 2026"
function absLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return `${ordinal(d.getDate())} ${d.toLocaleDateString("en-IN", { month: "short" })} ${d.getFullYear()}`;
}

// Combined: "1d ago (9th Jul 2026)" — or just the full date for older items.
function dateLabel(iso) {
  const abs = absLabel(iso);
  const rel = whenLabel(iso);
  return rel ? `${rel} (${abs})` : abs;
}

// Fresh = published today or yesterday (calendar-based) → gets the flashing badge.
function isFreshNews(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  if (isNaN(d)) return false;
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startYesterday = new Date(startToday);
  startYesterday.setDate(startYesterday.getDate() - 1);
  return d >= startYesterday && d <= new Date(now.getTime() + 60 * 1000);
}

const VIEW_KEY = "nc_newsroom_view";

const PAGE = 7;

export function CentralNewsroom() {
  const navigate = useNavigate();
  const [auths, setAuths] = useState([]);
  const [active, setActive] = useState(null); // null = All
  const [feed, setFeed] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [view, setView] = useState(() => {
    try { return localStorage.getItem(VIEW_KEY) || "row"; } catch { return "row"; }
  });
  useEffect(() => { try { localStorage.setItem(VIEW_KEY, view); } catch { /* ignore */ } }, [view]);

  useEffect(() => {
    getNewsColleges("Central").then((r) => setAuths(r.data.colleges || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true); setPage(0);
    getNewsFeed({ state: "Central", college_code: active || undefined, window: "all", limit: 120 })
      .then((r) => setFeed(r.data)).catch(() => setFeed({ items: [] }))
      .finally(() => setLoading(false));
  }, [active]);

  const total = useMemo(() => auths.reduce((n, a) => n + (a.count || 0), 0), [auths]);
  const activeAuth = useMemo(() => auths.find((a) => a.college_code === active), [auths, active]);
  const items = feed.items || [];
  const pages = Math.max(1, Math.ceil(items.length / PAGE));
  const p = Math.min(page, pages - 1);
  const slice = items.slice(p * PAGE, p * PAGE + PAGE);

  if (!auths.length) return null; // hide entirely when there is no central news

  return (
    <section className="border-b border-zinc-200 bg-white" data-testid="central-newsroom">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-[120px] pt-6 pb-12 sm:pt-8 sm:pb-16">
        {/* masthead */}
        <div className="relative overflow-hidden lg:grid lg:grid-cols-[2fr_1fr_1fr] lg:items-center flex flex-wrap items-end gap-4 border-b-2 border-[#09090B] pb-5 mb-6">
          <AnimatedBackdrop variant="aurora" colors={["#c7b6ff", "#a7d8ff"]} />
          <ParticleField variant="sparkles" count={12} color="#8b6bff" seed={11} />
          {/* Part 1 (50%) — masthead title */}
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-1.5">
              <Newspaper size={22} weight="fill" className="text-[#09090B]" />
              <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.25em] text-rose-600">
                <Broadcast size={12} weight="fill" className="animate-pulse" /> Live wire
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-none text-[#09090B] font-heading">
              Central Newsroom
            </h2>
            <p className="mt-2 text-sm text-zinc-500 max-w-xl">
              Official NEET-UG counselling notices, results & schedules straight from{" "}
              <b className="text-zinc-700">NMC · MCC · NBE · NTA</b>.
            </p>
          </div>
          {/* Part 2 (25%) — notify + colleges news */}
          <div className="relative z-20 flex flex-col items-center gap-2">
            <LiveFeedTyper />
            <SubscribeButton scope="central" variant="light" />
            <button onClick={() => navigate("/colleges")} data-testid="newsroom-view-tv"
              className="group inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#6043EF] hover:gap-2.5 transition-all">
              Colleges news <ArrowUpRight size={14} weight="bold" className="group-hover:rotate-45 transition-transform" />
            </button>
          </div>
          {/* Part 3 (25%) — TV */}
          <div className="relative z-10 hidden md:flex items-center justify-center">
            <div className="relative h-[124px] w-[220px]" data-testid="newsroom-tv-box">
              <NewsroomTV />
            </div>
          </div>
        </div>

        {/* authority filter pills + view toggle */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex flex-wrap gap-2" data-testid="newsroom-filters">
            {auths.map((a) => {
              const m = authMeta(a.college_code);
              return (
                <FilterPill key={a.college_code} active={active === a.college_code}
                  onClick={() => setActive(a.college_code)} testid={`newsroom-filter-${a.college_code}`}
                  label={m.label} sub={m.full} count={a.count} dot={m.dot} />
              );
            })}
            <FilterPill active={!active} onClick={() => setActive(null)} testid="newsroom-filter-all"
              label="All notices" count={total} dot="#09090B" />
          </div>
          <div className="inline-flex items-center gap-0.5 p-0.5 rounded-full bg-zinc-100 border border-zinc-200 shrink-0" data-testid="newsroom-view-toggle">
            <ViewBtn active={view === "row"} onClick={() => setView("row")} testid="newsroom-view-row" Icon={RowsIcon} label="Rows" />
            <ViewBtn active={view === "card"} onClick={() => setView("card")} testid="newsroom-view-card" Icon={SquaresFour} label="Cards" />
          </div>
        </div>

        {/* rows */}
        {loading ? (
          <div className="rounded-2xl border border-zinc-200 divide-y divide-zinc-100 overflow-hidden" data-testid="newsroom-loading">
            {Array.from({ length: PAGE }).map((_, i) => (
              <div key={i} className="h-[68px] bg-zinc-50 animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center rounded-2xl border border-dashed border-zinc-300 bg-zinc-50/60" data-testid="newsroom-empty">
            <p className="text-sm font-semibold text-zinc-600">No new notices in the last 3 months.</p>
            {activeAuth?.last_seen
              ? <p className="mt-1.5 text-xs text-zinc-400">Last update from {authMeta(active).label}: <b className="text-zinc-500">{absLabel(activeAuth.last_seen)}</b></p>
              : <p className="mt-1.5 text-xs text-zinc-400">We'll surface notices here the moment a new one is published.</p>}
          </div>
        ) : (
          <>
          {view === "card" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="newsroom-cards">
              {slice.map((it, i) => (
                <NewsCard key={`c-${it.id || i}`} it={it} idx={p * PAGE + i} anim={i} />
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden shadow-sm" data-testid="newsroom-rows">
              <div className="divide-y divide-zinc-100">
                {slice.map((it, i) => (
                  <NewsRow key={it.id || `${p}-${i}`} it={it} idx={p * PAGE + i} anim={i} />
                ))}
              </div>
            </div>
          )}
          </>
        )}

        {/* pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between gap-3 mt-4" data-testid="newsroom-pagination">
            <span className="text-[11px] font-semibold text-zinc-400 tabular-nums">
              {p * PAGE + 1}–{Math.min((p + 1) * PAGE, items.length)} of {items.length}
            </span>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setPage(Math.max(0, p - 1))} disabled={p === 0} data-testid="newsroom-prev"
                className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white border border-zinc-200 text-zinc-600 disabled:opacity-40 hover:border-[#6043EF] hover:text-[#6043EF] transition-colors">
                <CaretLeft size={13} weight="bold" />
              </button>
              {Array.from({ length: pages }).slice(0, 10).map((_, i) => (
                <button key={i} onClick={() => setPage(i)} data-testid={`newsroom-page-${i}`}
                  className={`h-8 min-w-[32px] px-1 rounded-full text-[11px] font-bold transition-all ${i === p ? "bg-[#09090B] text-white shadow-md scale-105" : "bg-white border border-zinc-200 text-zinc-500 hover:border-[#6043EF]"}`}>
                  {i + 1}
                </button>
              ))}
              <button onClick={() => setPage(Math.min(pages - 1, p + 1))} disabled={p === pages - 1} data-testid="newsroom-next"
                className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white border border-zinc-200 text-zinc-600 disabled:opacity-40 hover:border-[#6043EF] hover:text-[#6043EF] transition-colors">
                <CaretRight size={13} weight="bold" />
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes crRow{0%{opacity:0;transform:translateY(10px)}100%{opacity:1;transform:translateY(0)}}
        @keyframes crRise{0%{opacity:0;transform:translateY(7px)}100%{opacity:1;transform:translateY(0)}}
        @keyframes crBlink{0%,100%{opacity:1}50%{opacity:0}}
        @keyframes crFlash{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.3;transform:scale(1.3)}}
        @keyframes crFly{0%{opacity:0;transform:translateX(-16px) scale(.8)}60%{transform:translateX(3px) scale(1.06)}100%{opacity:1;transform:translateX(0) scale(1)}}
        @keyframes crGlow{0%,100%{box-shadow:0 0 0 0 rgba(244,63,94,0)}50%{box-shadow:0 0 0 3px rgba(244,63,94,.22)}}
        @media (prefers-reduced-motion: reduce){
          [data-fresh] , .cr-flash , .cr-fly { animation: none !important; }
        }
      `}</style>
    </section>
  );
}

function ViewBtn({ active, onClick, Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} title={`${label} view`} aria-pressed={active}
      className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all ${
        active ? "bg-[#09090B] text-white shadow-sm" : "text-zinc-500 hover:text-zinc-800"}`}>
      <Icon size={14} weight={active ? "fill" : "regular"} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

// Flying "Latest {type}" badge with a flashing red lightning icon — draws the eye
// to notices published today or yesterday. Uses crFly (entrance) + crFlash (icon).
function FreshBadge() {
  return (
    <span data-testid="newsroom-fresh-badge"
      className="cr-fly inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full text-white bg-gradient-to-r from-rose-600 to-red-500 shadow-[0_2px_8px_rgba(244,63,94,.4)] whitespace-nowrap"
      style={{ animation: "crFly .55s cubic-bezier(.2,.8,.2,1) both" }}>
      <Lightning size={11} weight="fill" className="cr-flash" style={{ animation: "crFlash 1s steps(2,end) infinite" }} />
      Latest
    </span>
  );
}

function NewsCard({ it, idx, anim }) {
  const m = authMeta(it.authority_code || (it.short || "").toLowerCase());
  const T = tpl(it.template);
  const confirmed = !!it.pub_confirmed;
  const fresh = confirmed && isFreshNews(it.published_at);
  const dateStr = confirmed ? dateLabel(it.published_at) : whenLabel(it.published_at);
  const rowHref = it.source_url || it.url;       // click goes to the listing/source page when we have one
  const dl = it.source_url ? it.url : null;      // …and the direct file stays on a separate Download action
  const openDl = (e) => { e.preventDefault(); e.stopPropagation(); if (dl) window.open(dl, "_blank", "noopener"); };
  return (
    <a href={rowHref} target="_blank" rel="noreferrer" data-testid={`newsroom-card-${idx}`}
      data-fresh={fresh ? "1" : undefined}
      className={`group relative rounded-2xl border bg-white overflow-hidden hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-zinc-300/50 transition-all duration-300 ${fresh ? "border-rose-300" : "border-zinc-200"}`}
      style={{ animation: `crRow .45s ease ${(anim % 3) * 0.06 + Math.floor(anim / 3) * 0.04}s both${fresh ? ", crGlow 2.4s ease-in-out infinite" : ""}` }}>
      <div className="relative h-[84px] overflow-hidden" style={{ background: `linear-gradient(135deg, ${m.dot}, ${shade(m.dot, -0.3)})` }}>
        <div aria-hidden className="absolute inset-x-[-25%] bottom-[-55%] h-[170%]"
          style={{ transform: "perspective(300px) rotateX(55deg)", transformOrigin: "bottom",
                   backgroundImage: "linear-gradient(rgba(255,255,255,.18) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.18) 1px,transparent 1px)",
                   backgroundSize: "22px 22px" }} />
        <span className="absolute top-2.5 right-2.5 flex items-center gap-1.5">
          {fresh && <FreshBadge />}
          <span className={`inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${T.cls}`}>
            <T.Icon size={11} weight="fill" /> {T.label}
          </span>
        </span>
        <div className="absolute bottom-2.5 left-3.5 z-10">
          <div className="text-white font-black text-2xl leading-none tracking-tight drop-shadow-md">{m.label}</div>
          <div className="text-white/80 text-[9px] font-semibold uppercase tracking-wider mt-1 truncate max-w-[200px]">{m.full}</div>
        </div>
      </div>
      <div className="p-4">
        {dateStr && <div className="text-[10px] font-mono text-zinc-400 mb-1.5">{dateStr}</div>}
        <p className="text-[13px] font-semibold leading-snug text-[#09090B] line-clamp-3 min-h-[54px] group-hover:text-[#6043EF] transition-colors">{it.title}</p>
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400 group-hover:text-[#6043EF] transition-colors">
            {dl ? "View all notices" : "Read notice"} <ArrowUpRight size={12} weight="bold" className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </span>
          {dl && (
            <span role="button" tabIndex={0} onClick={openDl} data-testid={`newsroom-download-${idx}`}
              className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-[#6043EF]/10 text-[#6043EF] hover:bg-[#6043EF] hover:text-white transition-colors">
              <DownloadSimple size={12} weight="bold" /> Download
            </span>
          )}
        </div>
      </div>
    </a>
  );
}

function NewsRow({ it, idx, anim }) {
  const m = authMeta(it.authority_code || (it.short || "").toLowerCase());
  const T = tpl(it.template);
  const confirmed = !!it.pub_confirmed;
  const fresh = confirmed && isFreshNews(it.published_at);
  const dateStr = confirmed ? dateLabel(it.published_at) : whenLabel(it.published_at);
  const rowHref = it.source_url || it.url;
  const dl = it.source_url ? it.url : null;
  const openDl = (e) => { e.preventDefault(); e.stopPropagation(); if (dl) window.open(dl, "_blank", "noopener"); };
  return (
    <a href={rowHref} target="_blank" rel="noreferrer" data-testid={`newsroom-item-${idx}`}
      data-fresh={fresh ? "1" : undefined}
      className={`group relative flex items-center gap-3 sm:gap-4 pl-5 pr-3 sm:pr-4 py-3.5 transition-colors ${fresh ? "bg-rose-50/60 hover:bg-rose-50" : "hover:bg-zinc-50"}`}
      style={{ animation: `crRow .4s ease ${anim * 0.06}s both` }}>
      <span aria-hidden className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: m.dot }} />
      <span className={`shrink-0 w-12 sm:w-14 text-center inline-flex items-center justify-center text-[10px] font-black uppercase tracking-wider px-1.5 py-1 rounded-lg ring-1 ${m.soft}`}>
        {m.label}
      </span>
      <span className="flex-1 min-w-0">
        <span className="flex items-center flex-wrap gap-2 mb-0.5">
          {fresh && <FreshBadge />}
          <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${T.cls}`}>
            <T.Icon size={10} weight="fill" /> {T.label}
          </span>
          {dateStr && <span className="text-[10px] font-mono text-zinc-400">{dateStr}</span>}
        </span>
        <span className="block text-[13px] font-semibold leading-snug text-[#09090B] truncate group-hover:text-[#6043EF] transition-colors">
          {it.title}
        </span>
      </span>
      {dl ? (
        <span role="button" tabIndex={0} onClick={openDl} data-testid={`newsroom-download-${idx}`}
          className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-full bg-[#6043EF]/10 text-[#6043EF] hover:bg-[#6043EF] hover:text-white transition-colors">
          <DownloadSimple size={13} weight="bold" /> <span className="hidden sm:inline">Download</span>
        </span>
      ) : (
        <ArrowUpRight size={16} weight="bold" className="shrink-0 text-zinc-300 group-hover:text-[#6043EF] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
      )}
    </a>
  );
}

function FilterPill({ active, onClick, label, sub, count, dot, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} title={sub}
      className={`group inline-flex items-center gap-2 h-9 pl-2.5 pr-3 rounded-full border text-xs font-bold transition-all ${
        active ? "border-transparent text-white shadow-md" : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400"}`}
      style={active ? { background: dot } : undefined}>
      <span className="w-2 h-2 rounded-full" style={{ background: active ? "rgba(255,255,255,.85)" : dot }} />
      <span className="uppercase tracking-wider">{label}</span>
      <span className={`text-[10px] font-black tabular-nums px-1.5 py-0.5 rounded-full ${active ? "bg-white/25" : "bg-zinc-100 text-zinc-500"}`}>{count}</span>
    </button>
  );
}
